import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import PassportHistoryModal, { PASSPORT_STATUS } from '../../components/passport/PassportHistoryModal';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';
import { fmtDate } from '../../components/requests/RequestActions';
import { listCollectedPassports } from '../../api/passportRegister';

const dash = (v) => v || <span className="text-gray-400">—</span>;

export default function CollectedPassports() {
  const filters = useCandidateFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [historyFor, setHistoryFor] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ['collected-passports', filters.params, search],
    queryFn: () => listCollectedPassports({ ...filters.params, search: search || undefined }),
  });
  const rows = useMemo(() => (query.data || []).map((c, i) => ({ ...c, no: i + 1 })), [query.data]);

  const columns = [
    { key: 'no', label: 'No' },
    { key: 'register_no', label: 'Register No', render: (row) => <span className="font-semibold">{row.register_no}</span> },
    { key: 'full_name', label: 'Name' },
    { key: 'passport_no', label: 'Passport No', render: (row) => dash(row.passport_no) },
    { key: 'nationality', label: 'Nationality', render: (row) => dash(row.nationality) },
    { key: 'vehicle_type', label: 'Vehicle Type', render: (row) => <span className="capitalize">{dash(row.vehicle_type)}</span> },
    { key: 'current_step', label: 'Current Workflow Stage', render: (row) => dash(row.current_step) },
    { key: 'contact_no', label: 'UAE Contact No', render: (row) => dash(row.contact_no) },
    { key: 'collected_at', label: 'Collected On', render: (row) => fmtDate(row.collected_at) },
    {
      key: 'collected_reason',
      label: 'Reason',
      render: (row) => (
        <div>
          {dash(row.collected_reason)}
          {row.collected_note && <div className="text-xs italic text-gray-500">"{row.collected_note}"</div>}
        </div>
      ),
    },
    {
      key: 'collected_by',
      label: 'Collected By',
      render: (row) => (
        <div className="text-xs text-gray-500">
          {dash(row.collected_by)}
          {row.approved_by && <div>Approved: {row.approved_by}</div>}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={() => setHistoryFor(row)}
          className={`rounded-md border px-2.5 py-1.5 text-xs font-medium ${PASSPORT_STATUS.collected.button}`}
        >
          History
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Passport Register" description="Passports currently held by the company, with their register numbers." />

      <CandidateFilterBar {...filters.barProps}>
        <Input placeholder="Search name, reference, passport, contact…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </CandidateFilterBar>

      <SearchSummary search={search} total={rows.length} rows={rows} plainCount={`${rows.length} passport${rows.length === 1 ? '' : 's'} held`} />
      <Table columns={withMatchColumn(columns, search)} data={rows} isLoading={query.isLoading} emptyMessage="No passports are currently collected." />

      <PassportHistoryModal candidate={historyFor} onClose={() => setHistoryFor(null)} />
    </div>
  );
}
